// client/src/hooks/useAppDownload.js
//
// Desktop-app (Electron) download state for the header.
//
// On mount it asks the backend for the current published version and compares
// it to the version this browser last saw (localStorage). `hasUpdate` drives
// the notification bell's red dot. `download()` fetches a short-lived presigned
// S3 URL, navigates to it, and marks the current version as seen (clearing the
// dot). The .exe + a latest.json manifest live in a private S3 bucket; the
// backend never streams the binary itself.

import { useState, useEffect } from 'react';
import { apiClient } from '../services/apiClient';

// localStorage key remembering the last app version this browser has "seen".
export const SEEN_VERSION_KEY = 'appDownloadSeenVersion';

// Human-readable file size for the download menu (e.g. "73.8 MB").
export const formatSize = (bytes) => {
  if (!bytes || Number.isNaN(bytes)) return null;
  const mb = bytes / (1024 * 1024);
  return mb >= 1024 ? `${(mb / 1024).toFixed(1)} GB` : `${mb.toFixed(1)} MB`;
};

export const useAppDownload = () => {
  const [info, setInfo] = useState(null);       // { version, filename, releasedAt, size }
  const [hasUpdate, setHasUpdate] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await apiClient.get('/app-download/version');
        if (cancelled || !data?.success) return;
        setInfo(data);
        const seen = localStorage.getItem(SEEN_VERSION_KEY);
        setHasUpdate(Boolean(data.version) && seen !== data.version);
      } catch (error) {
        // Non-fatal: no version info just means no bell/download prompt.
        console.warn('App version check failed:', error?.message);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const markSeen = () => {
    if (info?.version) {
      localStorage.setItem(SEEN_VERSION_KEY, info.version);
      setHasUpdate(false);
    }
  };

  const download = async () => {
    if (isDownloading) return;
    try {
      setIsDownloading(true);
      const { data } = await apiClient.get('/app-download/url');
      if (data?.success && data.url) {
        markSeen();
        // Navigate the browser to the presigned URL to start the download.
        window.location.href = data.url;
      } else {
        alert('Could not start the download. Please try again.');
      }
    } catch (error) {
      console.error('❌ App download failed:', error?.message);
      alert('Could not start the download. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  return { info, hasUpdate, isDownloading, download, markSeen };
};
